import React, { useState, useEffect, useContext, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { excavatorService, reportService } from "../services/api";
import { AuthContext } from "../context/AuthContext";
import moment from "moment";
import { useLanguage } from "../context/LanguageContext";
import PageContainer from "../components/PageContainer";
import ContentSection from "../components/ContentSection";
import WhatshotIcon from '@mui/icons-material/Whatshot';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Chip,
  Tabs,
  Tab,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Skeleton,
  Avatar,
  Stepper,
  Step,
  StepLabel,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DescriptionIcon from "@mui/icons-material/Description";
import BuildIcon from "@mui/icons-material/Build";
import UpdateIcon from "@mui/icons-material/Update";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import InfoIcon from "@mui/icons-material/Info";
import DownloadIcon from "@mui/icons-material/Download";
import BlockIcon from "@mui/icons-material/Block";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import { alpha } from "@mui/material/styles";

// StatCard: reusable for info cards
function StatCard({ icon, label, value, chip, tooltip, accent }) {
  return (
    <Card
      variant="outlined"
      sx={{
        boxShadow: accent ? 3 : 1,
        borderColor: accent
          ? (theme) => alpha(theme.palette.primary.main, 0.3)
          : undefined,
        background: accent
          ? (theme) => alpha(theme.palette.primary.light, 0.08)
          : undefined,
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: 6 },
        minHeight: 110,
      }}
    >
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {icon}
          <Typography variant="subtitle2" color="text.secondary">
            {label}
          </Typography>
        </Box>
        <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
          {chip ? (
            tooltip ? (
              <Tooltip title={tooltip}>{chip}</Tooltip>
            ) : (
              chip
            )
          ) : (
            <Typography variant="h6">{value}</Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

// HealthScore: simple indicator based on recent maintenance
function HealthScore({ score = 85 }) {
  let color = "success";
  let label = "Excellent";

  if (score < 60) {
    color = "error";
    label = "Poor";
  } else if (score < 80) {
    color = "warning";
    label = "Fair";
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <WhatshotIcon color={color} />
      <Typography variant="body2" color={`${color}.main`} fontWeight="600">
        {label} ({score}%)
      </Typography>
    </Box>
  );
}

// MaintenanceTimeline: stepper for maintenance/inspection history
function MaintenanceTimeline({ history }) {
  return (
    <Stepper alternativeLabel activeStep={history.length - 1} sx={{ mb: 2 }}>
      {history.map((item, idx) => (
        <Step key={idx} completed={idx < history.length - 1}>
          <StepLabel
            optional={<Typography variant="caption">{item.date}</Typography>}
            icon={<EventAvailableIcon />}
          >
            {item.label}
          </StepLabel>
        </Step>
      ))}
    </Stepper>
  );
}

const ExcavatorDetail = () => {
  const { id } = useParams();
  const { isAdmin } = useContext(AuthContext);
  const { t } = useLanguage();

  const [excavator, setExcavator] = useState(null);
  const [recentReports, setRecentReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [uvvStatus, setUvvStatus] = useState({
    status: "unknown",
    daysRemaining: 0,
    percentRemaining: 0,
    lastDate: null,
    nextDate: null,
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newHours, setNewHours] = useState("");
  const [hourUpdateNotes, setHourUpdateNotes] = useState("");
  const [hourUpdateLoading, setHourUpdateLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [lastMaintDialogOpen, setLastMaintDialogOpen] = useState(false);
  const [lastMaintDate, setLastMaintDate] = useState("");
  const [lastMaintLoading, setLastMaintLoading] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState("");
  const [operatorList, setOperatorList] = useState([]);
  const [scheduleDate, setScheduleDate] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [galleryModal, setGalleryModal] = useState({ open: false, index: 0 });
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  useEffect(() => {
    if (assignDialogOpen && operatorList.length === 0) {
      (async () => {
        try {
          const res = await import("../services/api");
          const users = await res.userService.getAll();
          // Always set as array
          const arr = Array.isArray(users.data)
            ? users.data
            : users.data?.data || [];
          setOperatorList(arr);
        } catch (e) {
          setOperatorList([]);
        }
      })();
    }
  }, [assignDialogOpen, operatorList.length]);

  useEffect(() => {
    if (excavator) {
      setAttachments(excavator.attachments || []);
    }
  }, [excavator]);

  const fetchExcavatorData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const excavatorResponse = await excavatorService.getById(id);
      const excavatorData = excavatorResponse.data;
      setExcavator(excavatorData);

      const reportsResponse = await reportService.getAll({
        excavatorId: id,
        limit: 10,
        sortBy: "createdAt",
        sortOrder: "desc",
      });

      if (
        reportsResponse.data &&
        reportsResponse.data.reports &&
        reportsResponse.data.reports.length > 0
      ) {
        const reports = reportsResponse.data.reports;
        setRecentReports(reports);

        const latest = reports[0];

        if (
          latest.operatingHours &&
          latest.operatingHours > excavatorData.hours
        ) {
          try {
            await excavatorService.update(id, {
              hours: latest.operatingHours,
            });
            excavatorData.hours = latest.operatingHours;
          } catch (updateErr) {
            console.error("Error updating excavator hours:", updateErr);
          }
        }

        if (latest.uvvInspectionDate) {
          calculateUVVStatus(latest.maintenanceDate, latest.uvvInspectionDate);
        } else {
          setUvvStatus({
            status: "unknown",
            daysRemaining: 0,
            percentRemaining: 0,
            lastDate: latest.maintenanceDate
              ? moment(latest.maintenanceDate)
              : null,
            nextDate: null,
          });
        }
      } else {
        if (excavatorData.lastMaintenanceDate) {
          const nextDate =
            excavatorData.nextMaintenanceDate ||
            moment(excavatorData.lastMaintenanceDate)
              .add(1, "year")
              .format("YYYY-MM-DD");

          calculateUVVStatus(excavatorData.lastMaintenanceDate, nextDate);
        }
      }
    } catch (err) {
      console.error("Error fetching excavator details:", err);
      setError(t("failedToLoadExcavatorDetails"));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchExcavatorData();
  }, [fetchExcavatorData]);

  const calculateUVVStatus = (lastDate, nextDate) => {
    if (!lastDate || !nextDate) {
      return setUvvStatus({
        status: "unknown",
        daysRemaining: 0,
        percentRemaining: 0,
        lastDate: null,
        nextDate: null,
      });
    }

    const lastMoment = moment(lastDate);
    const nextMoment = moment(nextDate);
    const now = moment();

    const totalDays = nextMoment.diff(lastMoment, "days");
    const daysRemaining = nextMoment.diff(now, "days");

    let percentRemaining = 100;
    if (totalDays > 0) {
      percentRemaining = Math.max(
        0,
        Math.round((daysRemaining / totalDays) * 100)
      );
    }

    let status;
    if (daysRemaining < 0) {
      status = "overdue";
    } else if (daysRemaining < 30) {
      status = "due";
    } else {
      status = "upToDate";
    }

    setUvvStatus({
      status,
      daysRemaining,
      percentRemaining,
      lastDate: lastMoment,
      nextDate: nextMoment,
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "operational":
        return "success";
      case "in maintenance":
        return "warning";
      case "in repair":
        return "error";
      default:
        return "default";
    }
  };

  const getUVVColor = (status) => {
    switch (status) {
      case "upToDate":
        return "success";
      case "due":
        return "warning";
      case "overdue":
        return "error";
      default:
        return "default";
    }
  };

  const handleHourUpdate = async () => {
    setHourUpdateLoading(true);
    try {
      await excavatorService.update(id, {
        hours: newHours,
        notes: hourUpdateNotes,
      });
      setNewHours("");
      setHourUpdateNotes("");
      fetchExcavatorData();
      setDialogOpen(false);
    } catch (err) {
      console.error("Error updating hours:", err);
    } finally {
      setHourUpdateLoading(false);
    }
  };

  const handleOutOfService = async () => {
    setStatusLoading(true);
    try {
      await excavatorService.update(id, { status: "in repair" });
      fetchExcavatorData();
    } finally {
      setStatusLoading(false);
    }
  };

  const handleAssignOperator = async () => {
    if (!selectedOperator) return;
    setStatusLoading(true);
    try {
      await excavatorService.update(id, { operator: selectedOperator });
      fetchExcavatorData();
      setAssignDialogOpen(false);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleScheduleMaintenance = async () => {
    if (!scheduleDate) return;
    setStatusLoading(true);
    try {
      await excavatorService.update(id, { nextMaintenanceDate: scheduleDate });
      fetchExcavatorData();
      setScheduleDialogOpen(false);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      await excavatorService.downloadPdf(id);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleAttachmentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadError("");
    try {
      const formData = new FormData();
      formData.append("attachment", file);
      await excavatorService.uploadAttachment(id, formData);
      fetchExcavatorData();
    } catch (err) {
      setUploadError(
        t("error") + ": " + (err?.response?.data?.message || err.message)
      );
    } finally {
      setUploading(false);
    }
  };

  // Separate images and other attachments
  const imageAttachments = attachments.filter((a) => a.isImage);
  const fileAttachments = attachments.filter((a) => !a.isImage);

  // Simulate health score and maintenance history for demo
  const maintenanceHistory = [
    {
      label: t("lastMaintenance"),
      date: excavator?.lastMaintenanceDate
        ? moment(excavator.lastMaintenanceDate).format("DD.MM.YYYY")
        : t("never"),
    },
    {
      label: t("nextMaintenance"),
      date: excavator?.nextMaintenanceDate
        ? moment(excavator.nextMaintenanceDate).format("DD.MM.YYYY")
        : t("never"),
    },
  ];

  if (loading) {
    return (
      <PageContainer>
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="rectangular" height={180} animation="wave" />
        </Box>
        <Grid container spacing={3} sx={{ mb: 2 }}>
          {[...Array(4)].map((_, i) => (
            <Grid item xs={12} md={3} key={i}>
              <Skeleton variant="rounded" height={110} animation="wave" />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={300} animation="wave" />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="error" gutterBottom>
            {error}
          </Typography>
          <Button
            variant="contained"
            onClick={fetchExcavatorData}
            sx={{ mt: 2 }}
          >
            {t("retry") || "Retry"}
          </Button>
        </Box>
      </PageContainer>
    );
  }

  // Hero/banner section
  const heroBg = `linear-gradient(90deg, #e3f2fd 0%, #fff 100%)`;

  return (
    <PageContainer>
      <Box
        sx={{
          background: heroBg,
          borderRadius: 3,
          p: { xs: 2, md: 5 },
          mb: 4,
          display: "flex",
          alignItems: { xs: "flex-start", md: "center" },
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
          boxShadow: 2,
          animation: "fadeIn 0.7s",
        }}
      >
        <Avatar
          variant="rounded"
          sx={{
            width: 80,
            height: 80,
            bgcolor: "primary.light",
            fontSize: 40,
            mb: { xs: 2, md: 0 },
          }}
        >
          <BuildIcon fontSize="inherit" />
        </Avatar>
        <Box sx={{ flex: 1, width: "100%" }}>
          <Typography
            variant="h4"
            fontWeight={700}
            gutterBottom
            sx={{ fontSize: { xs: 22, sm: 28, md: 32 } }}
          >
            {excavator?.manufacturer} {excavator?.model}
          </Typography>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{ fontSize: { xs: 14, sm: 16 } }}
          >
            {excavator?.serialNumber} • {excavator?.year}
          </Typography>
          <Box sx={{ mt: 1 }}>
            <HealthScore score={uvvStatus.percentRemaining || 85} />
          </Box>
        </Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{
            ml: { md: "auto" },
            width: { xs: "100%", sm: "auto" },
            mt: { xs: 2, md: 0 },
          }}
        >
          {excavator?.status === "in repair" ? (
            <Button
              variant="outlined"
              color="success"
              startIcon={<BuildIcon />}
              onClick={async () => {
                setStatusLoading(true);
                try {
                  await excavatorService.update(id, { status: "operational" });
                  fetchExcavatorData();
                } finally {
                  setStatusLoading(false);
                }
              }}
              disabled={statusLoading}
            >
              {statusLoading ? (
                <CircularProgress size={18} />
              ) : (
                t("setOperational")
              )}
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="error"
              startIcon={<BlockIcon />}
              onClick={handleOutOfService}
              disabled={statusLoading}
            >
              {statusLoading ? (
                <CircularProgress size={18} />
              ) : (
                t("outOfService")
              )}
            </Button>
          )}
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<EventAvailableIcon />}
            onClick={() => setScheduleDialogOpen(true)}
          >
            {t("scheduleMaintenance")}
          </Button>
          <Button
            variant="outlined"
            color="info"
            startIcon={<UpdateIcon />}
            onClick={() => setLastMaintDialogOpen(true)}
          >
            {t("setLastMaintenanceDate") || "Set Last Maintenance Date"}
          </Button>
          <Button
            variant="contained"
            color="info"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadPdf}
            disabled={pdfLoading}
          >
            {pdfLoading ? <CircularProgress size={18} /> : t("downloadPdf")}
          </Button>
        </Stack>
      </Box>

      <ContentSection
        title={t("excavatorInformation")}
        icon={<BuildIcon />}
        actions={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              component={Link}
              to={`/excavators/edit/${excavator?._id}`}
            >
              {t("edit")}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddCircleIcon />}
              component={Link}
              to={`/reports/create?excavatorId=${excavator?._id}`}
            >
              {t("createReport")}
            </Button>
            <Tooltip title={t("updateOperatingHours") || "Update Hours"}>
              <Button
                variant="outlined"
                color="info"
                startIcon={<UpdateIcon />}
                onClick={() => setDialogOpen(true)}
              >
                {t("updateHours")}
              </Button>
            </Tooltip>
          </Stack>
        }
      >
        <MaintenanceTimeline history={maintenanceHistory} />
        <Grid container spacing={3} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<InfoIcon color="primary" />}
              label={t("status")}
              chip={
                <Chip
                  label={t(excavator?.status || "unknown")}
                  color={getStatusColor(excavator?.status)}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              }
              tooltip={t(excavator?.status || "unknown")}
              accent
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<AccessTimeIcon color="primary" />}
              label={t("operatingHours")}
              value={excavator?.hours ?? "-"}
              accent
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<AssignmentTurnedInIcon color="primary" />}
              label={t("uvvStatus")}
              chip={
                <Chip
                  label={t(uvvStatus.status)}
                  color={getUVVColor(uvvStatus.status)}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              }
              tooltip={
                uvvStatus.nextDate
                  ? `${t("nextInspection")}: ${uvvStatus.nextDate.format(
                    "DD.MM.YYYY"
                  )}`
                  : t("noData")
              }
              accent
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={<LocationOnIcon color="primary" />}
              label={t("location")}
              value={excavator?.location || t("notSpecified")}
              accent
            />
          </Grid>
        </Grid>

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)}>
            <Tab label={t("overview")} />
            <Tab label={t("reports")} />
          </Tabs>
        </Box>

        {tabIndex === 0 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t("description")}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {excavator?.description || t("noDescription")}
            </Typography>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t("notes")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {excavator?.notes || t("noNotes")}
            </Typography>
          </Box>
        )}
        {tabIndex === 1 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t("recentReports")}
            </Typography>
            {recentReports.length === 0 ? (
              <Typography color="text.secondary">
                {t("noReportsFound")}
              </Typography>
            ) : (
              <TableContainer component={Card} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("date")}</TableCell>
                      <TableCell>{t("operator")}</TableCell>
                      <TableCell>{t("status")}</TableCell>
                      <TableCell>{t("actions")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentReports.map((report) => (
                      <TableRow key={report._id} hover>
                        <TableCell>
                          {moment(report.maintenanceDate).format("DD.MM.YYYY")}
                        </TableCell>
                        <TableCell>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                          >
                            <Avatar
                              sx={{ width: 28, height: 28, fontSize: 14 }}
                            >
                              {report.operator?.name
                                ? report.operator.name[0]
                                : "?"}
                            </Avatar>
                            {report.operator?.name || t("unknown")}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={t(report.status)}
                            color={
                              report.status === "signed" ? "success" : "warning"
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            component={Link}
                            to={`/reports/${report._id}`}
                            size="small"
                            variant="outlined"
                            startIcon={<DescriptionIcon />}
                          >
                            {t("view")}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}


        <Box sx={{ mt: 4, mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            <AttachFileIcon sx={{ mr: 1, verticalAlign: "middle" }} />{" "}
            {t("attachments")}
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="center"
            sx={{ mb: 2, width: "100%" }}
          >
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
              disabled={uploading}
            >
              {uploading ? t("loading") : t("add")}
              <input
                type="file"
                hidden
                onChange={handleAttachmentUpload}
                accept="*/*"
              />
            </Button>
            {uploadError && (
              <Typography color="error">{uploadError}</Typography>
            )}
          </Stack>
          {fileAttachments.length === 0 ? (
            <Typography color="text.secondary">
              {t("noDataAvailable")}
            </Typography>
          ) : (
            <Stack spacing={1}>
              {fileAttachments.map((file, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: { xs: "100%", sm: 400 },
                  }}
                >
                  <Button
                    href={file.url}
                    target="_blank"
                    rel="noopener"
                    startIcon={<AttachFileIcon />}
                    sx={{
                      flex: 1,
                      justifyContent: "flex-start",
                      minWidth: 0,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {file.originalname || file.name || file.url}
                  </Button>
                  {isAdmin && isAdmin() && (
                    <IconButton
                      aria-label="delete"
                      color="error"
                      size="small"
                      sx={{ ml: 1 }}
                      onClick={async () => {
                        if (
                          window.confirm(
                            t("confirmDeleteAttachment") ||
                            "Delete this attachment?"
                          )
                        ) {
                          await excavatorService.deleteAttachment(
                            id,
                            file.filename
                          );
                          fetchExcavatorData();
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Stack>
          )}
        </Box>

        <Typography variant="h6" sx={{ mb: 1 }}>
          <InsertPhotoIcon sx={{ mr: 1, verticalAlign: "middle" }} />{" "}
          {t("gallery")}
        </Typography>
        {imageAttachments.length === 0 ? (
          <Typography color="text.secondary">{t("noDataAvailable")}</Typography>
        ) : (
          <Stack
            direction="row"
            spacing={2}
            sx={{ flexWrap: "wrap", width: "100%" }}
          >
            {imageAttachments.map((img, idx) => (
              <Box
                key={idx}
                sx={{ position: "relative", display: "inline-block" }}
              >
                <Box
                  component="img"
                  src={img.url}
                  alt={img.originalname || "excavator image"}
                  sx={{
                    width: 120,
                    height: 80,
                    objectFit: "cover",
                    borderRadius: 2,
                    boxShadow: 1,
                    cursor: "pointer",
                    display: "block",
                  }}
                  onClick={() => setGalleryModal({ open: true, index: idx })}
                />
                {isAdmin && isAdmin() && (
                  <IconButton
                    aria-label="delete"
                    color="error"
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      bgcolor: "white",
                      p: 0.5,
                      boxShadow: 1,
                      zIndex: 2,
                    }}
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (
                        window.confirm(
                          t("confirmDeleteImage") || "Delete this image?"
                        )
                      ) {
                        await excavatorService.deleteAttachment(
                          id,
                          img.filename
                        );
                        fetchExcavatorData();
                      }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
          </Stack>
        )}
        {/* Gallery Modal/Slideshow */}
        {galleryModal?.open && (
          <Dialog
            open
            onClose={() => setGalleryModal({ open: false, index: 0 })}
            maxWidth="md"
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 2,
              }}
            >
              <IconButton
                onClick={() =>
                  setGalleryModal({
                    open: true,
                    index:
                      (galleryModal.index - 1 + imageAttachments.length) %
                      imageAttachments.length,
                  })
                }
              >
                {"<"}
              </IconButton>
              <Box
                component="img"
                src={imageAttachments[galleryModal.index].url}
                alt={imageAttachments[galleryModal.index].originalname}
                sx={{ maxHeight: 500, maxWidth: 700, borderRadius: 2 }}
              />
              <IconButton
                onClick={() =>
                  setGalleryModal({
                    open: true,
                    index: (galleryModal.index + 1) % imageAttachments.length,
                  })
                }
              >
                {">"}
              </IconButton>
            </Box>
            <DialogActions>
              <Button
                onClick={() => setGalleryModal({ open: false, index: 0 })}
              >
                {t("close")}
              </Button>
            </DialogActions>
          </Dialog>
        )}



        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
          <DialogTitle>{t("updateOperatingHours")}</DialogTitle>
          <DialogContent>
            <TextField
              label={t("currentHours")}
              type="number"
              value={newHours}
              onChange={(e) => setNewHours(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label={t("notes")}
              value={hourUpdateNotes}
              onChange={(e) => setHourUpdateNotes(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>{t("cancel")}</Button>
            <Button
              onClick={handleHourUpdate}
              variant="contained"
              disabled={hourUpdateLoading}
            >
              {hourUpdateLoading ? <CircularProgress size={20} /> : t("update")}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={assignDialogOpen}
          onClose={() => setAssignDialogOpen(false)}
        >
          <DialogTitle>{t("assignOperator")}</DialogTitle>
          <DialogContent>
            <TextField
              select
              label={t("operator")}
              value={selectedOperator}
              onChange={(e) => setSelectedOperator(e.target.value)}
              fullWidth
              SelectProps={{ native: true }}
              sx={{ mb: 2 }}
            >
              <option value="">{t("select")}</option>
              {operatorList.map((op) => (
                <option key={op._id} value={op._id}>
                  {op.name}
                </option>
              ))}
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAssignDialogOpen(false)}>
              {t("cancel")}
            </Button>
            <Button
              onClick={handleAssignOperator}
              variant="contained"
              disabled={statusLoading || !selectedOperator}
            >
              {statusLoading ? <CircularProgress size={20} /> : t("assign")}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={scheduleDialogOpen}
          onClose={() => setScheduleDialogOpen(false)}
        >
          <DialogTitle>{t("scheduleMaintenance")}</DialogTitle>
          <DialogContent>
            <TextField
              label={t("nextMaintenanceDate")}
              type="date"
              value={scheduleDate}
              onChange={(e) => setScheduleDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setScheduleDialogOpen(false)}>
              {t("cancel")}
            </Button>
            <Button
              onClick={handleScheduleMaintenance}
              variant="contained"
              disabled={statusLoading || !scheduleDate}
            >
              {statusLoading ? <CircularProgress size={20} /> : t("schedule")}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={lastMaintDialogOpen}
          onClose={() => setLastMaintDialogOpen(false)}
        >
          <DialogTitle>
            {t("setLastMaintenanceDate") || "Set Last Maintenance Date"}
          </DialogTitle>
          <DialogContent>
            <TextField
              label={t("lastMaintenanceDate") || "Last Maintenance Date"}
              type="date"
              value={lastMaintDate}
              onChange={(e) => setLastMaintDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setLastMaintDialogOpen(false)}>
              {t("cancel")}
            </Button>
            <Button
              onClick={async () => {
                setLastMaintLoading(true);
                try {
                  await excavatorService.update(id, {
                    lastMaintenanceDate: lastMaintDate,
                  });
                  fetchExcavatorData();
                  setLastMaintDialogOpen(false);
                } finally {
                  setLastMaintLoading(false);
                }
              }}
              variant="contained"
              disabled={lastMaintLoading || !lastMaintDate}
            >
              {lastMaintLoading ? <CircularProgress size={20} /> : t("update")}
            </Button>
          </DialogActions>
        </Dialog>
      </ContentSection>
    </PageContainer>
  );
};

export default ExcavatorDetail;
